import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { DocumentIcon, BarsArrowDownIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const formatValue = (value) => {
  const abs = Math.abs(Number(value) || 0);
  if (abs >= 1000) return `${(abs / 1000).toFixed(1)}k`;
  return abs.toFixed(1);
};

const BAR_SIZE = 24;
const BAR_CATEGORY_GAP = 10;
const MIN_CHART_HEIGHT = 180;

const OVERLAP_PCT = 0.75;

const FLOW_COLOR_IN = '#4ade80';
const FLOW_COLOR_OUT = '#f87171';

function OverlapBarShape(props) {
  const { x, y, width, height, payload, flowBarColor } = props;
  const budget = Math.abs(Number(payload?.budget) || 0);
  const flow = Math.abs(Number(payload?.flow) || 0);
  const achievementPct = payload?.achievement_pct != null ? Number(payload.achievement_pct) : (budget !== 0 ? (flow / budget) * 100 : 0);
  const maxVal = Math.max(budget, flow, 1);
  const budgetWidth = (budget / maxVal) * width;
  const flowWidth = (flow / maxVal) * width;
  const radius = 4;
  const barHeight = height * OVERLAP_PCT;
  const gap = height - barHeight;
  const grayY = y;
  const flowY = y + gap;
  const fill = flowBarColor ?? FLOW_COLOR_OUT;
  const flowCenterX = x + flowWidth / 2;
  const flowCenterY = flowY + barHeight / 2;
  const showPctInBar = flowWidth >= 36;
  return (
    <g>
      <rect
        x={x}
        y={grayY}
        width={Math.max(budgetWidth, 0)}
        height={barHeight}
        fill="#b8bcc4"
        rx={radius}
        ry={radius}
      />
      <rect
        x={x}
        y={flowY}
        width={Math.max(flowWidth, 0)}
        height={barHeight}
        fill={fill}
        rx={radius}
        ry={radius}
      />
      {showPctInBar && (
        <text
          x={flowCenterX}
          y={flowCenterY}
          fill="white"
          fontSize={10}
          fontWeight={600}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {budget === 0 && flow > 0 ? '∞' : `${achievementPct.toFixed(0)}%`}
        </text>
      )}
    </g>
  );
}

const SORT_OPTIONS = [
  { value: 'budget', label: 'By budget' },
  { value: 'flow', label: 'By flow' },
];

const CardBudgetCategories = ({ boardData, loading = false, filterLabel, selectedType = 'Out' }) => {
  const flowBarColor = selectedType === 'In' ? FLOW_COLOR_IN : FLOW_COLOR_OUT;
  const [sortBy, setSortBy] = useState('budget');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { chartData, domainMax, hasData, chartHeight } = useMemo(() => {
    const raw = boardData?.by_l1 || [];
    const mapped = raw.map((item) => {
      const budget = Math.abs(Number(item.budget_total) || 0);
      const flow = Math.abs(Number(item.flow_total) || 0);
      const achievementPct = Number(item.achievement_pct) ?? (budget !== 0 ? (flow / budget) * 100 : 0);
      return {
        name: item.l1 || '',
        budget,
        flow,
        max: Math.max(budget, flow, 0),
        achievement_pct: achievementPct,
      };
    });
    const sorted =
      sortBy === 'flow'
        ? [...mapped].sort((a, b) => b.flow - a.flow)
        : [...mapped].sort((a, b) => b.budget - a.budget);
    const max = sorted.length
      ? Math.max(...sorted.flatMap((d) => [d.budget, d.flow]), 1)
      : 1;
    const chartHeight =
      sorted.length > 0
        ? Math.max(MIN_CHART_HEIGHT, sorted.length * (BAR_SIZE + BAR_CATEGORY_GAP))
        : MIN_CHART_HEIGHT;

    return {
      chartData: sorted,
      domainMax: max,
      hasData: sorted.length > 0 && sorted.some((d) => d.budget > 0 || d.flow > 0),
      chartHeight,
    };
  }, [boardData?.by_l1, sortBy]);

  if (loading) {
    return (
      <div className="h-76 min-w-[300px] min-h-[250px] bg-white shadow-lg rounded-xl p-3 flex flex-col relative overflow-hidden">
        <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
          <div className="h-6 bg-gray-400 rounded animate-pulse w-32" />
          <div className="h-5 bg-gray-300 rounded animate-pulse w-24" />
        </div>
        <div className="flex-1 relative min-h-[200px]">
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 bg-gray-200 rounded animate-pulse w-full" />
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
            <SpinnerLoad />
          </div>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const budgetVal = Math.abs(Number(d.budget) || 0);
    const flowVal = Math.abs(Number(d.flow) || 0);
    const pctDisplay =
      budgetVal === 0 && flowVal > 0
        ? '∞'
        : d.achievement_pct != null
        ? `${Number(d.achievement_pct).toFixed(1)}%`
        : '—';
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <div className="font-semibold text-gray-800 mb-1">{d.name}</div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-600">Flow</span>
          <span className="font-medium">{formatValue(d.flow)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-600">Budget</span>
          <span className="font-medium">{formatValue(d.budget)}</span>
        </div>
        <div className="flex justify-between gap-4 border-t border-gray-100 pt-1 mt-1">
          <span className="text-gray-600">%</span>
          <span className="font-medium">{pctDisplay}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[280px] min-w-[300px] bg-white shadow-lg rounded-xl p-3 flex flex-col relative">
      <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-md font-semibold">by Category</span>
          {filterLabel != null && filterLabel !== '' && (
            <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
              {filterLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-[#b8bcc4]" />
              Budget
            </span>
            <span className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: flowBarColor }}
              />
              Flow
            </span>
          </div>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Sort"
            >
              <BarsArrowDownIcon className="w-4 h-4" />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[120px]">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setSortBy(opt.value);
                      setDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs font-medium ${
                      sortBy === opt.value ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative" style={{ height: hasData ? chartHeight : MIN_CHART_HEIGHT }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 88, bottom: 4, left: 0 }}
              barCategoryGap={BAR_CATEGORY_GAP}
            >
              <XAxis type="number" domain={[0, domainMax]} hide />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                width={72}
                interval={0}
                tickFormatter={(v) => (v && v.length > 12 ? v.slice(0, 11) + '…' : v) || ''}
              />
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 5 }} />
              <Bar
                dataKey="max"
                barSize={BAR_SIZE}
                shape={<OverlapBarShape flowBarColor={flowBarColor} />}
              >
                <LabelList
                  dataKey="max"
                  position="right"
                  fill="#374151"
                  fontSize={10}
                  content={(labelProps) => {
                    if (labelProps == null) return null;
                    const index = labelProps.index ?? 0;
                    const row = chartData[index];
                    if (!row) return null;
                    const text = `${formatValue(row.flow)} / ${formatValue(row.budget)}`;
                    const vb = labelProps.viewBox ?? labelProps;
                    const x = (vb.x ?? 0) + (vb.width ?? 0) + 4;
                    const y = (vb.y ?? 0) + (vb.height ?? 0) / 2;
                    return (
                      <text x={x} y={y} fill="#374151" fontSize={10} dominantBaseline="middle">
                        {text}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-3">
                <DocumentIcon className="w-16 h-16 mx-auto" />
              </div>
              <div className="text-gray-500 text-sm font-medium">No data available</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardBudgetCategories;
